import { VALTYPE_ANY, VALTYPES } from '../../../../data/interfaces';
import { createColorMappingAspect, createColorMappingConfigs } from '../../helpers/colorMappingInfo';
import {
  createConfigParamBoolean,
  createConfigParamLabelSpace,
  createConfigParamMultipleChoice,
  createConfigParamTypeRange,
} from '../../helpers/configParams';
import { TpChartManifest } from '../interfaces';
import { ParallelCoordinates } from './ParallelCoordinates';

export const manifestParallelCoordinates: TpChartManifest = {
  id: 'ParallelCoordinates',
  name: 'Parallel coordinates plot',
  description: `Displays a number of numerical properties by plotting each property on a vertical axis,
and visualising all entities in the table by connecting the corresponding property values on the axis via lines.
Optionally, these can be colored by another property. By changing the lineMode, commonAxes and xaxisspread settings, 
you can make the plot behave like a strip plot for each property.`,
  component: ParallelCoordinates,
  canvasBased: true,
  groups: [
    { groupId: 'axes', groupName: 'Axes' },
    { groupId: 'appearance', groupName: 'Appearance' },
  ],
  aspects: [
    createColorMappingAspect('color', 'Line color data', 'axes', 0.5, 'lines'),
    {
      id: 'hoverLabel',
      name: 'Hover label',
      description: 'Text to be displayed when the mouse hovers over an axis coordinate.',
      groupId: 'axes',
      required: false,
      dataType: VALTYPE_ANY,
    },
  ],
  compoundAspects: [
    {
      id: 'coordinates',
      name: 'Coordinates',
      description: 'Set of properties to be displayed as parallel coordinates.',
      groupId: 'axes',
      required: true,
      dataType: [VALTYPES.NUMBER, VALTYPES.INTEGER],
      minCount: 2,
    },
  ],
  configParams: [
    {
      id: 'sortBy',
      name: 'Sort properties by',
      groupId: 'axes',
      description: 'How to sort the properties',
      paramType: createConfigParamMultipleChoice([
        { id: 'user', name: 'User selection' },
        { id: 'avgValInc', name: 'Increasing average value' },
        { id: 'avgValDec', name: 'Decreasing average value' },
      ]),
    },
    {
      id: 'commonAxes',
      name: 'Use common axis',
      description: 'If set, all properties share the same range on the axes.',
      groupId: 'axes',
      paramType: createConfigParamBoolean(false),
    },
    {
      id: 'lineMode',
      name: 'Line mode',
      groupId: 'axes',
      description:
        'Determines for which items to draw a line. Note that drawing all lines is best only used with xaxisspread "collapsed" mode.',
      paramType: createConfigParamMultipleChoice([
        { id: 'all', name: 'All items' },
        { id: 'highlighted', name: 'Only highlighted items' },
        { id: 'none', name: 'No items' },
      ]),
      hidden: true,
    },
    ...createColorMappingConfigs('color', 'Color', 'appearance'),
    {
      id: 'labelSpace',
      name: 'Label space',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the different properties.',
      groupId: 'appearance',
      paramType: createConfigParamLabelSpace(),
    },
    {
      id: 'pointSizeFactor',
      name: 'Point size',
      groupId: 'appearance',
      description: 'Scaling factor for the overall size of the points in the plot.',
      paramType: createConfigParamTypeRange(0.2, 4, 0.1, 1),
    },
    {
      id: 'opacity',
      name: 'Opacity',
      description: `Line opacity.
In case a plot contains many overlapping lines, lowering the opacity (i.e. increasing transparency)
can help reveal the data structure.`,
      groupId: 'appearance',
      paramType: createConfigParamTypeRange(0.1, 1, 0.1, 0.7),
    },
    {
      id: 'unselectedOpacity',
      name: 'Unselected opacity',
      groupId: 'appearance',
      description: `Unselected items opacity.`,
      paramType: createConfigParamTypeRange(0.01, 1, 0.1, 0.5),
    },
    {
      id: 'qualityMode',
      name: 'Point quality',
      groupId: 'appearance',
      description: `The draw quality of the points. Set to performance or auto for better dashboard performance with large datasets.`,
      paramType: createConfigParamMultipleChoice([
        { id: 'quality', name: 'High quality' },
        { id: 'performance', name: 'Fast performance' },
        { id: 'auto', name: 'Automatic based on data size' },
      ]),
      hidden: true,
    },
    {
      id: 'xaxisspread',
      name: 'Point spread on X axis',
      groupId: 'appearance',
      description: `Collapse or spread points on the X axis with random jitter just like in a strip plot.`,
      paramType: createConfigParamMultipleChoice([
        { id: 'collapsed', name: 'Collapse' },
        { id: 'spread', name: 'Spread' },
      ]),
      hidden: true,
    },
  ],
  examples: [
    {
      id: 'parallelcoords_1',
    },
    {
      id: 'parallelcoords_2',
    },
    {
      id: 'parallelcoords_3',
    },
  ],
};
